import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export type MobileThemeKey = 'tech' | 'pink' | 'white';

const THEME_KEY = 'home_mobile_theme';
const THEME_CHANGE_EVENT = 'mobile-theme-change';

/** 讀取儲存的主題，新用戶預設 white（白色簡約）*/
export function readMobileTheme(): MobileThemeKey {
  const v = localStorage.getItem(THEME_KEY) as MobileThemeKey | null;
  return v === 'tech' || v === 'pink' || v === 'white' ? v : 'white';
}

/** 從 Supabase 讀取使用者儲存的主題 */
export async function loadThemeFromCloud(userId: string): Promise<MobileThemeKey | null> {
  try {
    const { data, error } = await supabase
      .from('user_tokens')
      .select('theme')
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data?.theme) return null;
    const t = data.theme as MobileThemeKey;
    return t === 'tech' || t === 'pink' || t === 'white' ? t : null;
  } catch {
    return null;
  }
}

/** 將主題儲存到 Supabase（需要已登入） */
export async function saveThemeToCloud(userId: string, theme: MobileThemeKey): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_tokens')
      .upsert({ user_id: userId, theme }, { onConflict: 'user_id' });
    return !error;
  } catch {
    return false;
  }
}

/**
 * 全域共用 hook：讀取 / 寫入主題
 * 透過 window 自定義事件廣播，讓所有訂閱元件同步更新
 */
export function useMobileTheme() {
  const [theme, setThemeState] = useState<MobileThemeKey>(readMobileTheme);

  // 監聽其他元件發出的主題切換事件
  useEffect(() => {
    const handler = (e: Event) => {
      const newTheme = (e as CustomEvent<MobileThemeKey>).detail;
      setThemeState(newTheme);
    };
    window.addEventListener(THEME_CHANGE_EVENT, handler);

    // 掛載時同步 html 屬性
    document.documentElement.setAttribute('data-mobile-theme', readMobileTheme());

    return () => window.removeEventListener(THEME_CHANGE_EVENT, handler);
  }, []);

  const setTheme = (t: MobileThemeKey) => {
    localStorage.setItem(THEME_KEY, t);
    document.documentElement.setAttribute('data-mobile-theme', t);
    setThemeState(t);
    // 廣播給所有其他訂閱元件（Footer、Dashboard 等）
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: t }));
  };

  return { theme, setTheme };
}

/** 各頁面手機版通用主題設定 */
export interface MobilePageTheme {
  pageBg: string;
  filterActive: string;
  filterInactive: string;
  primaryBtn: string;
  accentText: string;
  iconBg: string;
  iconColor: string;
  loadingColor: string;
  ctaGradient: string;
  tagBg: string;
  emptyIconBg: string;
  emptyIconColor: string;
  sectionAccent: string;
  cardBg: string;
  inputSearch: string;
  statValueColor: string;
  /* ── 新增 ── */
  heroOverlay: string;       // hero section 疊色
  contentAreaBg: string;     // 主內容區背景
  filterCardBg: string;      // 搜尋 / 篩選卡片背景
  filterCardBorder: string;  // 篩選卡片邊框
  filterLabelColor: string;  // 篩選標籤文字色
  searchBg: string;          // 搜尋框背景
  searchBorder: string;      // 搜尋框邊框
  searchText: string;        // 搜尋框文字（含 placeholder）
  inputBg: string;           // 一般輸入框背景
  inputBorder: string;       // 一般輸入框邊框
  inputText: string;         // 一般輸入框文字
  textPrimary: string;       // 主要文字
  textSecondary: string;     // 次要文字
  dividerColor: string;      // 分隔線
  cardBorder: string;        // 內容卡片邊框
  heroBadgeBg: string;       // hero badge 背景
  heroBadgeText: string;     // hero badge 文字
  sectionTitle: string;      // 區塊標題
}

export function getMobilePageTheme(theme: MobileThemeKey): MobilePageTheme {
  switch (theme) {
    case 'pink':
      return {
        pageBg: 'bg-rose-50',
        filterActive: 'bg-rose-400 text-white shadow-sm',
        filterInactive: 'bg-white text-gray-700 hover:bg-rose-50 border border-rose-100',
        primaryBtn: 'bg-rose-400 hover:bg-rose-500 text-white',
        accentText: 'text-rose-400',
        iconBg: 'bg-rose-50',
        iconColor: 'text-rose-400',
        loadingColor: 'text-rose-300',
        ctaGradient: 'from-rose-400 to-rose-500',
        tagBg: 'bg-rose-400',
        emptyIconBg: 'bg-rose-50',
        emptyIconColor: 'text-rose-300',
        sectionAccent: 'text-rose-400',
        cardBg: 'bg-white',
        inputSearch: 'focus:border-rose-300',
        statValueColor: 'text-rose-400',
        heroOverlay: 'linear-gradient(to bottom, rgba(159,18,57,0.25) 0%, rgba(159,18,57,0.15) 50%, rgba(244,63,94,0.45) 100%)',
        contentAreaBg: 'bg-rose-50',
        filterCardBg: 'bg-white',
        filterCardBorder: 'border-rose-100',
        filterLabelColor: 'text-gray-700',
        searchBg: 'bg-white',
        searchBorder: 'border-rose-100',
        searchText: 'text-gray-700 placeholder:text-rose-300',
        inputBg: 'bg-white',
        inputBorder: 'border-rose-200',
        inputText: 'text-gray-800',
        textPrimary: 'text-gray-900',
        textSecondary: 'text-rose-400',
        dividerColor: 'bg-rose-100',
        cardBorder: 'border-rose-100',
        heroBadgeBg: 'bg-white/85 border-rose-200',
        heroBadgeText: 'text-rose-500',
        sectionTitle: 'text-gray-900',
      };
    case 'white':
      return {
        pageBg: 'bg-gray-50',
        filterActive: 'bg-gray-900 text-white shadow-sm',
        filterInactive: 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200',
        primaryBtn: 'bg-gray-900 hover:bg-gray-800 text-white',
        accentText: 'text-teal-600',
        iconBg: 'bg-teal-50',
        iconColor: 'text-teal-500',
        loadingColor: 'text-teal-500',
        ctaGradient: 'from-gray-800 to-gray-900',
        tagBg: 'bg-gray-800',
        emptyIconBg: 'bg-teal-50',
        emptyIconColor: 'text-teal-400',
        sectionAccent: 'text-teal-600',
        cardBg: 'bg-white',
        inputSearch: 'focus:border-teal-400',
        statValueColor: 'text-teal-600',
        heroOverlay: 'linear-gradient(to bottom, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.18) 50%, rgba(0,0,0,0.55) 100%)',
        contentAreaBg: 'bg-white',
        filterCardBg: 'bg-white',
        filterCardBorder: 'border-gray-200',
        filterLabelColor: 'text-gray-700',
        searchBg: 'bg-white',
        searchBorder: 'border-gray-200',
        searchText: 'text-gray-700 placeholder:text-gray-400',
        inputBg: 'bg-white',
        inputBorder: 'border-gray-300',
        inputText: 'text-gray-800',
        textPrimary: 'text-gray-900',
        textSecondary: 'text-gray-500',
        dividerColor: 'bg-gray-200',
        cardBorder: 'border-gray-200',
        heroBadgeBg: 'bg-white/85 border-gray-200',
        heroBadgeText: 'text-gray-700',
        sectionTitle: 'text-gray-900',
      };
    default: // tech
      return {
        pageBg: 'bg-gray-950',
        filterActive: 'bg-teal-500 text-white shadow-teal-500/30 shadow-md',
        filterInactive: 'bg-gray-800 text-gray-300 border-gray-700 hover:border-teal-500/50 hover:bg-gray-700',
        primaryBtn: 'bg-teal-500 hover:bg-teal-400 text-white',
        accentText: 'text-teal-400',
        iconBg: 'bg-teal-500/15',
        iconColor: 'text-teal-400',
        loadingColor: 'text-teal-400',
        ctaGradient: 'from-teal-600 to-teal-700',
        tagBg: 'bg-teal-500',
        emptyIconBg: 'bg-teal-500/15',
        emptyIconColor: 'text-teal-400',
        sectionAccent: 'text-teal-400',
        cardBg: 'bg-gray-800',
        inputSearch: 'focus:border-teal-400',
        statValueColor: 'text-teal-400',
        heroOverlay: 'linear-gradient(to bottom, rgba(19,78,74,0.55) 0%, rgba(0,0,0,0.55) 50%, rgba(3,7,18,0.88) 100%)',
        contentAreaBg: 'bg-gray-950',
        filterCardBg: 'bg-gray-800',
        filterCardBorder: 'border-gray-700',
        filterLabelColor: 'text-gray-300',
        searchBg: 'bg-gray-800',
        searchBorder: 'border-gray-600',
        searchText: 'text-white placeholder:text-gray-500',
        inputBg: 'bg-gray-800',
        inputBorder: 'border-gray-600',
        inputText: 'text-white',
        textPrimary: 'text-white',
        textSecondary: 'text-gray-400',
        dividerColor: 'bg-gray-700',
        cardBorder: 'border-gray-700',
        heroBadgeBg: 'bg-teal-400/20 border-teal-300/30',
        heroBadgeText: 'text-teal-100',
        sectionTitle: 'text-white',
      };
  }
}

/** 是否為手機環境（用於決定是否套用 mobile theme） */
export function isMobileEnv(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < 1024;
}
